import { Alert, Snackbar } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import CaseDataUpdateModal from '../components/Case/CaseDataUpdateModal';
import DisparityDetails from '../components/Case/DisparityDetails';
import ResourceDetails from '../components/Case/ResourceDetails.tsx';
import SelectionPanel from '../components/Case/SelectionPanel/SelectionPanel.tsx';
import StatuteDeleteModal from '../components/Case/StatuteDeleteModal.tsx';
import { useCaseWorkspace } from '../hooks/useCaseWorkspace';
import { useCaseApi } from '../services/api/useCase.api';

function CaseDetails() {
  const { caseId } = useParams<{ caseId?: string }>();
  const queryClient = useQueryClient();
  const { recalculateDisparities, updateCase } = useCaseApi();
  const {
    caseData,
    selectedDisparityId,
    selectedResourceId,
    onDisparitySelect,
    onResourceSelect,
    onUnselect,
    isSsbEnabled,
  } = useCaseWorkspace();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDataUpdateModal, setShowDataUpdateModal] = useState(false);
  const [showStatuteDeleteModal, setShowStatuteDeleteModal] = useState(false);

  const {
    mutate: recalculateCaseDataMutation,
    isPending: isUpdatingCase,
    isSuccess: isCaseDataUpdated,
  } = useMutation({
    mutationKey: ['recalculateCaseData', caseId],
    mutationFn: () => recalculateDisparities(Number(caseId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', caseId], exact: false });
      queryClient.invalidateQueries({ queryKey: ['caseDisparities'], exact: false });
      onUnselect();
    },
    onError: (error) => {
      setErrorMessage(error?.message || 'Failed to recalculate case data. Please try again.');
      setSnackbarOpen(true);
    },
  });

  const { mutate: deleteStatutesMutation } = useMutation({
    mutationKey: ['deletedStatuteNames', caseId],
    mutationFn: () => updateCase(Number(caseId), { deleted_statute_names: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', caseId], exact: false });
      queryClient.invalidateQueries({ queryKey: ['caseDisparities'], exact: false });
      onUnselect();
    },
  });

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleCloseDataUpdateModal = () => {
    setShowDataUpdateModal(false);
    queryClient.invalidateQueries({ queryKey: ['caseExportData', caseId], exact: false });
  };

  const handleCloseStatuteDeleteModal = () => {
    setShowStatuteDeleteModal(false);
    queryClient.invalidateQueries({ queryKey: ['caseExportData', caseId], exact: false });
    if (!caseId) return;
    deleteStatutesMutation();
  };

  const handleCaseUpdateData = () => {
    if (!caseId) return;
    recalculateCaseDataMutation();
  };

  const [prevCaseData, setPrevCaseData] = useState(caseData);
  if (caseData !== prevCaseData) {
    setPrevCaseData(caseData);

    if (caseData) {
      if (caseData.has_disparities_to_recalculate) {
        setShowDataUpdateModal(true);
      }

      if (caseData.deleted_statute_names && caseData.deleted_statute_names.length > 0) {
        setShowStatuteDeleteModal(true);
      }
    }
  }

  return (
    <>
      <SelectionPanel
        caseData={caseData}
        onDisparitySelect={onDisparitySelect}
        onResourceSelect={onResourceSelect}
        selectedDisparityId={selectedDisparityId}
        selectedResourceId={selectedResourceId}
        onDisparityDelete={onUnselect}
        isSsbEnabled={isSsbEnabled}
      />
      {selectedResourceId ? (
        <ResourceDetails caseData={caseData} resourceId={selectedResourceId} />
      ) : (
        <DisparityDetails
          caseData={caseData}
          disparityId={selectedDisparityId ?? undefined}
          onDisparityDelete={onUnselect}
          onDisparitySelect={onDisparitySelect}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity='error' sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      <CaseDataUpdateModal
        open={showDataUpdateModal}
        onKeep={handleCloseDataUpdateModal}
        onUpdate={handleCaseUpdateData}
        onDone={handleCloseDataUpdateModal}
        isUpdating={isUpdatingCase}
        isUpdateFinished={isCaseDataUpdated}
      />

      <StatuteDeleteModal
        open={showStatuteDeleteModal}
        onDone={handleCloseStatuteDeleteModal}
        statuteList={caseData?.deleted_statute_names}
      />
    </>
  );
}

export default CaseDetails;
